import { useEffect } from 'react';
import {
  Box,
  FormattedDateTime,
  Panel,
  PanelSection,
  ProgressBar,
  Stack,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { useAnalytics } from '@/services/analytics';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useOnceCallback, useOnceWhen } from '@/hooks/use-once';
import {
  agentUsedPercent,
  useAgentUsageCommonEventProps,
  useAgentUsageMeter,
  type AgentUsageMeter,
} from '@/features/billing-usage';

/**
 * Chat surfaces stay clean below this percent of included usage; at or past it
 * the input pill and in-thread card appear. 75 vs 78 is still open with product —
 * the earlier trigger is the safer default and this is the only line to change.
 */
export const AGENT_USAGE_SIGNAL_PERCENT = 75;

const PILL_BAR_STYLE = { inlineSize: '3.5rem' };

type AgentUsageSignal = {
  meter: AgentUsageMeter;
  /** Derived from raw credits, so it keeps counting past 100. */
  percent: number;
};

/**
 * The single gate behind both chat usage surfaces. Fails closed: no meter
 * (flag off, load error, still loading) or usage below the threshold means
 * the chat renders exactly as it does today.
 */
export function useAgentUsageSignal(): AgentUsageSignal | null {
  const { meter } = useAgentUsageMeter();
  if (meter == null) {
    return null;
  }
  const percent = agentUsedPercent(meter);
  if (percent < AGENT_USAGE_SIGNAL_PERCENT) {
    return null;
  }
  return { meter, percent };
}

/**
 * The compact usage pill in the chat input's control row: mini bar plus the
 * used percent, with the full explanation in a tooltip. Text turns critical
 * once the included amount is crossed — during the beta nothing blocks.
 */
// Chat sessions whose pill-shown event already fired. Module scope, not a mount
// ref: the pill's slot in the input control row yields to the memory pill
// (`showMemoryPill ? null : <AgentUsagePill/>`), so it can remount several times
// within one session — only a new session instance (new conversation) may fire
// the event again. Keyed on the instance, not `session.id`, because the id is
// undefined until the backend session initializes; WeakSet so ended sessions
// stay collectable.
const pillShownSessions = new WeakSet<CopilotSession>();

type AgentUsagePillProps = {
  /** The chat session the pill reports for; the shown event fires once per session. */
  session: CopilotSession;
};

export function AgentUsagePill({ session }: AgentUsagePillProps) {
  const signal = useAgentUsageSignal();
  const { track } = useAnalytics();
  const workspace = useCurrentWorkspace();
  const buildCommonProps = useAgentUsageCommonEventProps();
  const trackTooltipOnce = useOnceCallback((meter: AgentUsageMeter) => {
    void track('agentUsage', 'chatPillTooltipOpened', buildCommonProps(meter));
  });

  // Reports the pill to analytics once per chat session. The WeakSet is the only
  // latch — deliberately not useOnceWhen, whose per-mount guard would never
  // re-arm when a new session replaces the old one in place (/new, or the home
  // page's new-session-on-submit) without remounting the pill. Keying on the
  // session lets the effect re-fire for the new instance, while the meter
  // refetching after every exchange (a new signal identity) stays deduped.
  useEffect(() => {
    // The workspace check keeps the latch honest: track() drops events while the
    // workspace context is still resolving, so only a post that goes out may
    // consume this session's slot in the WeakSet.
    if (signal == null || workspace == null || pillShownSessions.has(session)) {
      return;
    }
    pillShownSessions.add(session);
    void track('agentUsage', 'chatPillShown', {
      ...buildCommonProps(signal.meter),
      Critical: signal.percent >= 100,
    });
  }, [session, signal, workspace, track, buildCommonProps]);

  if (signal == null) {
    return null;
  }
  const { percent } = signal;
  const reached = percent >= 100;

  const handleTooltipOpen = () => {
    // Same latch rule as the view events: a hover while the workspace context is
    // still resolving would be dropped by track(), so it must not consume the
    // once-latch. The guard stays outside trackTooltipOnce for that reason.
    if (workspace == null) {
      return;
    }
    trackTooltipOnce(signal.meter);
  };

  const tooltipContent = reached
    ? `You've used ${percent}% of your included usage of the Agent this cycle. Nothing is blocked, and this isn't charged during the beta.`
    : `You've used ${percent}% of your included usage of the Agent this cycle. Usage past the included amount isn't charged or blocked during the beta.`;

  return (
    <Tooltip content={tooltipContent} isCustom={true}>
      {(context) => (
        <Box
          display="flex"
          alignItems="center"
          gap="050"
          data-test-agent-usage-pill
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps({ onMouseEnter: handleTooltipOpen, onFocus: handleTooltipOpen })}
        >
          <Box style={PILL_BAR_STYLE}>
            <ProgressBar
              value={Math.min(percent, 100)}
              ariaLabel={`${percent}% of the Agent's included usage`}
              size="sm"
            />
          </Box>
          <Text size="xs" color={reached ? 'critical' : 'secondary'} weight={reached ? 'medium' : undefined}>
            {percent}% of included usage
          </Text>
        </Box>
      )}
    </Tooltip>
  );
}

type AgentUsageThreadCardProps = {
  signal: AgentUsageSignal;
};

/**
 * The in-thread usage card, shown at the bottom of the conversation once the
 * threshold is crossed. Deliberately light: no dismiss state, no modal.
 */
export function AgentUsageThreadCard({ signal }: AgentUsageThreadCardProps) {
  const { meter, percent } = signal;
  const reached = percent >= 100;
  const { track } = useAnalytics();
  const workspace = useCurrentWorkspace();
  const buildCommonProps = useAgentUsageCommonEventProps();

  // Once per mount — the card is mounted per conversation view and the meter
  // refetches after every exchange while it stays up. Waits for the workspace
  // context so the once-latch can't consume on a dropped track() call.
  useOnceWhen(workspace != null, () => {
    void track('agentUsage', 'threadCardShown', {
      ...buildCommonProps(meter),
      Variant: reached ? 'reached' : 'approaching',
    });
  });

  return (
    <Panel data-test-agent-usage-thread-card>
      <PanelSection>
        <Stack gap="100">
          <Text weight="semibold">{reached ? 'Included usage reached' : 'Included usage approaching'}</Text>
          <ProgressBar
            value={Math.min(percent, 100)}
            ariaLabel={`${percent}% of the Agent's included usage`}
            size="sm"
          />
          <Text size="sm" color="secondary" data-test-agent-usage-thread-reset>
            Your included usage resets on{' '}
            <FormattedDateTime withTooltip={false} date={new Date(meter.reset_date).getTime()} format="medium" />.
          </Text>
          <Text size="sm" color="secondary">
            Nothing changes today. Usage past the included amount isn't charged or blocked during the beta.
          </Text>
        </Stack>
      </PanelSection>
    </Panel>
  );
}
